import { ChangeEventHandler, FC, useMemo, useState } from 'react';
import { AccordionOptionCard } from '@/components/ui/AccordionOptionCard';
import { AttributeWithValues } from '@/types/creator';
import {
	Accordion,
	Badge,
	Flex,
	SimpleGrid,
	Text,
	TextInput,
} from '@mantine/core';
import { AttributeValue } from '@/api/AttributeValueApi';
import { getFileSrc } from '@/utils/utilities';
import SearchIcon from '@/assets/icons/search.svg?react';
import APP_CONFIG from '@/configs/appConfig';

interface Props {
	attribute: AttributeWithValues;
	selectedAttributeValue: AttributeValue | null | undefined;
	handleSelectAttributeValue: (attributeValue: AttributeValue) => () => void;
}

export const SingleAttribute: FC<Props> = ({
	attribute,
	selectedAttributeValue,
	handleSelectAttributeValue,
}) => {
	const [searchFilter, setSearchFilter] = useState('');
	const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = ({
		target,
	}) => {
		setSearchFilter(target.value);
	};

	const isFilterable =
		attribute.values.length > APP_CONFIG.FILTERABLE_ATTRIBUTE_VALUES_THRESHOLD;

	const filteredValues = useMemo(() => {
		return attribute.values.filter((attrValue) => {
			if (!searchFilter) return true;
			if (!isFilterable) return true;

			return attrValue.value
				.toLowerCase()
				.includes(searchFilter.toLowerCase().trim());
		});
	}, [attribute, searchFilter]);

	return (
		<Accordion.Item value={attribute.id}>
			<Accordion.Control>
				<Flex justify="space-between">
					{attribute.name}
					{!!selectedAttributeValue && (
						<Badge>
							<Text size="sm">{selectedAttributeValue.value}</Text>
						</Badge>
					)}
				</Flex>
			</Accordion.Control>
			<Accordion.Panel>
				{attribute.description && (
					<Text size="md" c="dimmed">
						{attribute.description}
					</Text>
				)}
				{isFilterable && (
					<TextInput
						rightSection={<SearchIcon />}
						placeholder="Szukaj po nazwie"
						className="flex-grow-1"
						mt="sm"
						value={searchFilter}
						onChange={handleChangeSearch}
					/>
				)}
				<SimpleGrid py="md" cols={3} spacing="xs">
					{filteredValues.map((attributeValue) => {
						const isSelected = selectedAttributeValue?.id === attributeValue.id;

						return (
							<AccordionOptionCard
								key={attributeValue.id}
								id={attributeValue.id}
								name={attributeValue.displayName || attributeValue.value}
								description={attributeValue.description}
								imagePath={getFileSrc(attributeValue?.image?.path)}
								isSelected={isSelected}
								onSelect={handleSelectAttributeValue(attributeValue)}
								shopUrl={attributeValue.shopUrl}
							/>
						);
					})}
				</SimpleGrid>
			</Accordion.Panel>
		</Accordion.Item>
	);
};
